"use client";

import React, { useMemo, useRef } from "react";
import { Table } from "antd";

/* ===================== DUMMY DATA ===================== */

const dynamicYears = ["2026-27", "2027-28", "2028-29", "2029-30", "2030-31"];

const headerStyle = {
    style: {
        backgroundColor: "#1589c9",
        color: "#ffffff",
        fontWeight: 700,
        textAlign: "center",
    },
};

/* ===================== CELL STYLE ===================== */

const getCellStyle = (row) => {
    const base = {
        color: "#475467",
        fontWeight: 600,
    };

    if (row.isGrand) {
        return {
            style: {
                ...base,
                backgroundColor: "#fde68a",
                fontWeight: "bold",
            },
        };
    }

    if (row.level === 1) {
        return { style: { ...base, backgroundColor: "#d1d5db" } };
    }

    if (row.level === 2) {
        return { style: { ...base, backgroundColor: "#f3f4f6" } };
    }

    if (row.level === 3) {
        return { style: { ...base, backgroundColor: "#ffffff" } };
    }

    return { style: base };
};

/* ===================== DATA BUILDERS ===================== */

const createValues = () => ({
    orderOS: 100,
    Q4_25_26: 20,
    Balance_01_04_26: 80,
    Q1_26_27: 10,
    Q2_26_27: 15,
    Q3_26_27: 20,
    Q4_26_27: 25,
    "2026-27": 70,
    "2027-28": 80,
    "2028-29": 90,
    "2029-30": 100,
    "2030-31": 110,
    balance: 50,
});

const projects = ["Proj 1", "Proj 2", "Proj 3"];

const buildProjects = (parentKey) =>
    projects.map((p, i) => ({
        key: `${parentKey}-proj-${i}`, // ✅ UNIQUE
        level: 3,
        bs: "",
        project: p,
        ...createValues(),
    }));

const buildSector = (name, parentKey) => ({
    key: `${parentKey}-sector-${name}`, // ✅ UNIQUE
    level: 2,
    bs: name,
    project: "",
    ...createValues(),
    children: buildProjects(`${parentKey}-sector-${name}`),
});

const buildClearOrders = () => ({
    key: "clear",
    level: 1,
    bs: "Clear Order Subtotal",
    children: [
        buildSector("Power Sector (MS)", "clear"),
        buildSector("Industry Sector (ID)", "clear"),
        buildSector("Intl Oprn (IO)", "clear"),
    ],
});

const buildDirectOrders = () => ({
    key: "direct",
    level: 1,
    bs: "Direct Order Subtotal",
    children: buildProjects("direct"),
});

const buildAnticipated = () => ({
    key: "anticipated",
    level: 1,
    bs: "Anticipated Subtotal",
    children: [
        buildSector("Power Sector", "anticipated"),
        buildSector("Ind Sector", "anticipated"),
        buildSector("Intl Oprn", "anticipated"),
    ],
});

const buildHoldOrders = () => ({
    key: "hold",
    level: 1,
    bs: "Hold Orders Subtotal",
    children: buildProjects("hold"),
});

const buildGrandTotal = () => ({
    key: "grand",
    bs: "Grand Total",
    isGrand: true,
    ...createValues(),
});

/* ===================== COMPONENT ===================== */

const ExecutionPlan = () => {
    /* ✅ FIX: stable data (prevents duplication bug) */
    const dataRef = useRef(null);

    if (!dataRef.current) {
        dataRef.current = [
            buildClearOrders(),
            buildDirectOrders(),
            buildAnticipated(),
            buildHoldOrders(),
            buildGrandTotal(),
        ];
    }

    const columns = useMemo(() => {
        const liquidationChildren = [
            {
                title: "Q4 25-26",
                dataIndex: "Q4_25_26",
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => {
                    const base = getCellStyle(row);
                    return {
                        style: {
                            ...base.style,

                        },
                    };
                },
            },
            {
                title: "Balance (01-04-26)",
                dataIndex: "Balance_01_04_26",
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => {
                    const base = getCellStyle(row);
                    return {
                        style: {
                            ...base.style,
                            backgroundColor: "#fde68a",
                            fontWeight: 600,
                        },
                    };
                },
            },
            {
                title: "Q1 26-27",
                dataIndex: "Q1_26_27",
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            },
            {
                title: "Q2 26-27",
                dataIndex: "Q2_26_27",
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            },
            {
                title: "Q3 26-27",
                dataIndex: "Q3_26_27",
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            },
            {
                title: "Q4 26-27",
                dataIndex: "Q4_26_27",
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            },
        ];

        dynamicYears.forEach((year) => {
            liquidationChildren.push({
                title: year,
                dataIndex: year,
                align: "center",
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            });
        });

        return [
            {
                title: "BS",
                dataIndex: "bs",
                fixed: "left",
                width: 200,
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
                render: (text, row) => {
                    const paddingLeft =
                        row.level === 1 ? 8 :
                            row.level === 2 ? 24 :
                                row.level === 3 ? 40 : 8;

                    return <div style={{ paddingLeft }}>{text}</div>;
                },
            },
            {
                title: "Project Name",
                dataIndex: "project",
                fixed: "left",
                width: 200,
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            },
            {
                title: "Order O/S",
                dataIndex: "orderOS",
                align: "center",
                width: 200,
                onHeaderCell: () => headerStyle,
                onCell: (row) => getCellStyle(row),
            },
            {
                title: "Liquidation Plan",
                children: liquidationChildren,
                onHeaderCell: () => headerStyle,
            },
            {
                title: "Balance",
                dataIndex: "balance",
                fixed: "right",
                align: "center",
                width: 200,
                onHeaderCell: () => headerStyle,
                onCell: (row) => {
                    const base = getCellStyle(row);
                    return {
                        style: {
                            ...base.style,
                            backgroundColor: "#fde68a",
                            fontWeight: 700,
                        },
                    };
                },
            },
        ];
    }, []);

    return (
        <div className="p-4 bg-white">
            <Table
                bordered
                columns={columns}
                dataSource={dataRef.current}
                pagination={false}
                scroll={{ x: 2000, y: 600 }}
                sticky
                rowKey="key"
                expandable={{ defaultExpandAllRows: false }}
            />
        </div>
    );
};

export default ExecutionPlan;